import { WritableSignal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';

export interface UploadRequest {
  url: string;
  formData: FormData;
}

/** Factory function type that builds an {@link UploadRequest} for a given file.*/
export type UploadFactory = ( file: File ) => UploadRequest;

/**
 * Represents a file item being managed by the uploader.
 */
export interface FileUploadItem {
  /** The native File object. */
  file: File;
  /** Reactive signal tracking the upload progress (0-100). */
  progress: WritableSignal<number>;
  /** Reactive signal tracking the current status of the file. */
  status: WritableSignal<'pending' | 'uploading' | 'completed' | 'error' | 'cancelled'>;
  /** Subject used to trigger cancellation of the upload request. */
  cancel$: Subject<void>;
  /** Reactive signal containing translation keys for any errors encountered. */
  errorKeys: WritableSignal<string[]>;
  /** Reactive signal indicating the type of error (validation or upload). */
  errorType: WritableSignal<'validation' | 'upload' | undefined>;
  /** Reactive signal holding the HTTP error response for upload failures.
   * Consumers can read this in `(uploadComplete)` via `event.filesWithErrors[i].httpError()`
   * to forward the backend error to a user-facing message handler. */
  httpError: WritableSignal<HttpErrorResponse | undefined>;
}

/**
 * Canonical MIME type per supported file extension. Used as the primary value
 * for the input `accept` attribute and for MIME-based validation.
 *
 * Browsers may report alternative MIME values for some types (e.g. xlsx as
 * `application/zip`, csv as `application/vnd.ms-excel`, eml as
 * `application/octet-stream`). Those still pass validation via the extension
 * fallback in {@link FILE_EXTENSIONS_CONST}.
 */
export const MIME_TYPES_CONST = {
  avi: 'video/x-msvideo',
  bmp: 'image/bmp',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  eml: 'message/rfc822',
  gif: 'image/gif',
  heic: 'image/heic',
  jpeg: 'image/jpeg',
  mov: 'video/quicktime',
  mp4: 'video/mp4',
  odt: 'application/vnd.oasis.opendocument.text',
  pdf: 'application/pdf',
  png: 'image/png',
  tiff: 'image/tiff',
  txt: 'text/plain',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  zip: 'application/zip',
} as const;

/**
 * Allowed file extensions per supported type. Used both for the input `accept`
 * attribute (so the OS picker filters reliably even when MIME reporting is
 * unreliable) and as a validation fallback when the browser-reported MIME does
 * not match the canonical one in {@link MIME_TYPES_CONST}.
 */
export const FILE_EXTENSIONS_CONST = {
  avi: ['.avi'],
  bmp: ['.bmp'],
  csv: ['.csv'],
  doc: ['.doc'],
  docx: ['.docx'],
  eml: ['.eml'],
  gif: ['.gif'],
  heic: ['.heic', '.heif'],
  jpeg: ['.jpeg', '.jpg'],
  mov: ['.mov'],
  mp4: ['.mp4'],
  odt: ['.odt'],
  pdf: ['.pdf'],
  png: ['.png'],
  tiff: ['.tif', '.tiff'],
  txt: ['.txt'],
  xls: ['.xls'],
  xlsx: ['.xlsx'],
  zip: ['.zip'],
} as const satisfies Record<keyof typeof MIME_TYPES_CONST, readonly string[]>;

export interface UploadCompleteEvent {
  hasErrors: boolean;
  filesWithErrors: FileUploadItem[];
}

/** Type for file status. */
export type StatusType = 'pending' | 'uploading' | 'completed' | 'error' | 'cancelled';
/** Type for supported file extension keys. */
export type MimeTypes = keyof typeof MIME_TYPES_CONST;
/** Type for browser-standard MIME strings. */
export type BrowserMimeType = typeof MIME_TYPES_CONST[keyof typeof MIME_TYPES_CONST];
